import { useState } from "react";
import { Calculator } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OddsCalculator() {
  const [stake, setStake] = useState("10");
  const [odd, setOdd] = useState("2.10");

  const mise = parseFloat(String(stake).replace(",", "."));
  const cote = parseFloat(String(odd).replace(",", "."));
  const valid = !isNaN(mise) && mise > 0 && !isNaN(cote) && cote > 1;

  const gainTotal = valid ? mise * cote : null;
  const gainNet = valid ? mise * (cote - 1) : null;
  const proba = valid ? 100 / cote : null;

  return (
    <div
      data-testid="odds-calculator"
      className="my-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-[#0B1F3A]">
        <Calculator className="h-5 w-5 text-[#2BB08A]" aria-hidden="true" />
        Calculateur de cotes
      </h3>
      <p className="mb-5 text-sm text-slate-500">
        Saisissez une mise et une cote décimale : le résultat s'affiche instantanément.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="odds-stake" className="mb-1 block text-sm font-medium text-slate-700">
            Mise (€)
          </label>
          <input
            id="odds-stake"
            data-testid="odds-input-stake"
            type="number"
            min="0"
            step="0.5"
            inputMode="decimal"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
        <div>
          <label htmlFor="odds-odd" className="mb-1 block text-sm font-medium text-slate-700">
            Cote décimale
          </label>
          <input
            id="odds-odd"
            data-testid="odds-input-odd"
            type="number"
            min="1.01"
            step="0.01"
            inputMode="decimal"
            value={odd}
            onChange={(e) => setOdd(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
      </div>

      {!valid && (
        <p data-testid="odds-error" className="mt-4 text-sm font-medium text-[#78350F]">
          Saisissez une mise positive et une cote supérieure à 1,00.
        </p>
      )}

      {valid && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div data-testid="odds-result-total" className="rounded-lg bg-[#F3F5F8] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Gain total
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#0B1F3A]">
              {fmt(gainTotal)} €
            </p>
            <p className="mt-1 text-xs text-slate-500">mise × cote (mise incluse)</p>
          </div>
          <div data-testid="odds-result-net" className="rounded-lg bg-[#F3F5F8] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Gain net
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#0B1F3A]">
              {fmt(gainNet)} €
            </p>
            <p className="mt-1 text-xs text-slate-500">mise × (cote − 1)</p>
          </div>
          <div data-testid="odds-result-proba" className="rounded-lg bg-[#E8F8F3] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#064E3B]">
              Probabilité implicite
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#064E3B]">
              {proba.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %
            </p>
            <p className="mt-1 text-xs text-[#064E3B]/70">1 ÷ cote, marge incluse</p>
          </div>
        </div>
      )}
      <p className="mt-4 text-xs leading-relaxed text-slate-500">
        Rappel : la probabilité implicite intègre la marge de l'opérateur. Elle surestime donc
        légèrement la probabilité réelle de l'événement.
      </p>
    </div>
  );
}
